import type { LocationQuery } from "vue-router";

/**
 * Auth state that must survive across browser tabs uses localStorage (auth connection,
 * passwordless ID token, bridge token). Social login tokens already live in localStorage
 * via the Auth0 SPA SDK cacheLocation setting.
 *
 * Transient per-login-flow state uses sessionStorage (pending login query). It only
 * needs to survive the same-tab redirect to Auth0 and back; keeping it per-tab avoids
 * concurrent login flows in different tabs clobbering each other's stashed query params.
 */
export const AUTH_CONNECTION_STORAGE_KEY = "dashboard_auth_connection";
/** Bridge auth token is only used for MM wallet sign-in via bridge hydra OIDC QRcode token for agentic login. */
const BRIDGE_AUTH_TOKEN_STORAGE_KEY = "dashboard_bridge_auth_token";
const PASSWORDLESS_ID_TOKEN_STORAGE_KEY = "dashboard_passwordless_id_token";
/** Same-tab only — see module comment above. */
const PENDING_LOGIN_QUERY_KEY = "dashboard_passwordless_login_query";

export function setStoredAuthConnection(authConnection: string): void {
  localStorage.setItem(AUTH_CONNECTION_STORAGE_KEY, authConnection);
}

export function getStoredAuthConnection(): string | undefined {
  return localStorage.getItem(AUTH_CONNECTION_STORAGE_KEY) ?? undefined;
}

export function clearStoredAuthConnection(): void {
  localStorage.removeItem(AUTH_CONNECTION_STORAGE_KEY);
}

export function getBridgeAuthToken(): string | null {
  return localStorage.getItem(BRIDGE_AUTH_TOKEN_STORAGE_KEY);
}

export function setBridgeAuthToken(token: string): void {
  localStorage.setItem(BRIDGE_AUTH_TOKEN_STORAGE_KEY, token);
}

export function getPasswordlessIdToken(): string | null {
  return localStorage.getItem(PASSWORDLESS_ID_TOKEN_STORAGE_KEY);
}

export function setPasswordlessIdToken(token: string): void {
  localStorage.setItem(PASSWORDLESS_ID_TOKEN_STORAGE_KEY, token);
}

export function clearPasswordlessIdToken(): void {
  localStorage.removeItem(PASSWORDLESS_ID_TOKEN_STORAGE_KEY);
}

export function setPendingLoginQuery(query: LocationQuery): void {
  sessionStorage.setItem(PENDING_LOGIN_QUERY_KEY, JSON.stringify(query));
}

export function getPendingLoginQuery(): LocationQuery {
  const raw = sessionStorage.getItem(PENDING_LOGIN_QUERY_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as LocationQuery;
  } catch {
    return {};
  }
}

export function clearPendingLoginQuery(): void {
  sessionStorage.removeItem(PENDING_LOGIN_QUERY_KEY);
}

export function clearPasswordlessSession(): void {
  clearPasswordlessIdToken();
  clearPendingLoginQuery();
}

export function clearBridgeAuthToken(): void {
  localStorage.removeItem(BRIDGE_AUTH_TOKEN_STORAGE_KEY);
}

/** Clear non-SPA auth state: wallet bridge token and auth0-js passwordless session. */
export function clearAlternateAuthSessions(): void {
  clearBridgeAuthToken();
  clearPasswordlessSession();
}

export function clearAuthStorage(): void {
  clearStoredAuthConnection();
  clearAlternateAuthSessions();
}
